import type { BusinessStructure } from '@paid-ui/enums/business';
import type { EntityStatus } from '@paid-ui/enums/entity';
import type { CompanyDirectorsStructure, UserGroupType, UserStatus } from '@paid-ui/enums/user';
import type { Address } from '@paid-ui/schemas/zod/address';
import type { LinkedBusinessRelation, UserDetail, UserGroupRole } from '@paid-ui/types/parties';

import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import { PermissionName, PermissionType, UserGroupRelation } from '@paid-ui/enums/user';

export interface UserInGroup {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  homeAddress?: Address;
  relation: UserGroupRelation;
  status: EntityStatus;
  userStatus: UserStatus;
  groupDefaultSignatory: boolean;
  roles: UserGroupRole[];
  canReactivate?: boolean;
  isNominee?: boolean;
  permission?: Record<string, unknown[]>;
}

export interface OfficerInBusiness {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  homeAddress?: Address;
  isEmailVerified: boolean;
  isMobileVerified: boolean;
}

export interface BusinessInGroup {
  id: string;
  displayName: string;
  businessStructure: BusinessStructure;
  companyDirectorsStructure: CompanyDirectorsStructure;
  provideBuildingServices: boolean;
  businessSetupCompleted: boolean;
  linkedBusinesses?: LinkedBusinessRelation[];
  registeredName: string;
  registeredForGst: boolean;
  registeredAddress?: Address;
  abn?: string;
  acn?: string;
  builderLicenceNumber?: string;
  contactNumber?: string;
  earlyReleaseDiscountRate?: number;
}

const initialState = {
  id: '',
  displayName: '',
  type: '' as UserGroupType,
  requiredGroupSignatories: 0,
  users: [] as UserInGroup[],
  usersWithPermissions: [] as UserInGroup[],
  linkedBusinesses: [] as LinkedBusinessRelation[],
  business: undefined as BusinessInGroup | undefined,
  signatories: [] as UserInGroup[],
  relation: '' as UserGroupRelation,
  permissions: [] as UserGroupRole[],
  permission: {
    canManagePayment: false,
    canManageContract: false,
    canManagePrimeContract: false,
    canManageSecondaryContract: false,
    canManageGenericContract: false,
    canSignContract: false,
    canViewContract: false,
    canManageUser: false,
  },
};

export type UserGroup = Omit<typeof initialState, 'relation' | 'permissions' | 'permission'>;

export const userGroupManager = proxy(initialState);

devtools(userGroupManager, {
  name: 'Default User Group',
  enabled: false,
});

export const resetUserGroupManager = () => {
  Object.assign(userGroupManager, initialState);
};

export const saveUserGroup = (profile?: UserDetail | null) => {
  if (!profile?.groupRelation) return;
  const { relation, group, permission } = profile.groupRelation;
  const permissions = permission?.roles ?? [];
  const flatPermissions = permissions.flatMap((p) => p.permissions);
  const users = group.users ?? [];

  const signatories = users.filter((user) => user.relation !== UserGroupRelation.EMPLOYEE);
  const permissionsMap = {} as Partial<Record<PermissionName, PermissionType[]>>;

  for (const { permission, permissionType } of flatPermissions) {
    if (permission in permissionsMap) {
      permissionsMap[permission]?.push(permissionType);
    } else {
      permissionsMap[permission] = [permissionType];
    }
  }
  const managePaymentPermissions = permissionsMap[PermissionName.MANAGE_PAYMENT] ?? [];
  const manageContractPermissions = permissionsMap[PermissionName.MANAGE_CONTRACT] ?? [];
  const signContractPermissions = permissionsMap[PermissionName.MANAGE_SIGNING] ?? [];
  const viewContractPermissions = permissionsMap[PermissionName.VIEW_CONTRACT] ?? [];
  const manageUserPermissions = permissionsMap[PermissionName.MANAGE_USER] ?? [];

  Object.assign(userGroupManager, {
    relation,
    signatories,
    permissions: permission?.roles ?? [],
    permission: {
      canManagePayment: managePaymentPermissions.length > 0,
      canManageContract: manageContractPermissions.length > 0,
      canManagePrimeContract: manageContractPermissions.includes(PermissionType.CONTRACT_PRIME),
      canManageSecondaryContract: manageContractPermissions.includes(
        PermissionType.CONTRACT_SECONDARY,
      ),
      canManageGenericContract: manageContractPermissions.includes(PermissionType.CONTRACT),
      canSignContract: signContractPermissions.length > 0,
      canViewContract: viewContractPermissions.length > 0,
      canManageUser: manageUserPermissions.length > 0,
    },
    ...group,
    linkedBusinesses: group.business?.linkedBusinesses ?? [],
  });
};

export const saveUsersWithPermissions = (users: UserInGroup[]) => {
  userGroupManager.usersWithPermissions = users;
};
